import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MessageCircle, Share2, Plus, Camera, X,
  Dog, ChevronLeft, User, MapPin, Send, Image,
  MoreHorizontal, Bookmark, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Upload helper
async function uploadToCloudinary(file, folder = 'puppoto/posts') {
  // Get signature from backend
  const sigRes = await fetch(`${API_URL}/api/puppoto/cloudinary/signature?folder=${folder}`, {
    credentials: 'include'
  });
  
  if (!sigRes.ok) {
    throw new Error('Failed to get upload signature');
  }
  
  const sig = await sigRes.json();
  
  // Upload to Cloudinary
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('signature', sig.signature);
  form.append('folder', sig.folder);
  
  const uploadRes = await fetch(
    `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
    { method: 'POST', body: form }
  );
  
  if (!uploadRes.ok) {
    throw new Error('Upload failed');
  }
  
  return uploadRes.json();
}

// Create Post Modal
const CreatePostModal = ({ isOpen, onClose, dogs, onPostCreated }) => {
  const [selectedDog, setSelectedDog] = useState(dogs[0]?.id || '');
  const [caption, setCaption] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    try {
      for (const file of files.slice(0, 4)) { // Max 4 images
        const result = await uploadToCloudinary(file);
        setImages(prev => [...prev, result.secure_url]);
      }
      toast.success('Photos uploaded!');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handlePost = async () => {
    if (!selectedDog) {
      toast.error('Please select a dog');
      return;
    }
    if (images.length === 0 && !caption.trim()) {
      toast.error('Add a photo or caption');
      return;
    }

    setPosting(true);
    try {
      const res = await fetch(`${API_URL}/api/puppoto/feed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          dog_id: selectedDog,
          caption,
          images,
          image_url: images[0]
        })
      });

      if (res.ok) {
        toast.success('Posted! 🐕');
        onPostCreated();
        onClose();
        setCaption('');
        setImages([]);
      } else {
        toast.error('Failed to post');
      }
    } catch (error) {
      toast.error('Failed to post');
    } finally {
      setPosting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            New Post
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Dog Selector */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#7A7571] mb-2">Posting as</label>
          <select
            value={selectedDog}
            onChange={e => setSelectedDog(e.target.value)}
            className="w-full h-10 px-3 rounded-xl border border-[#EBE8E0] bg-white"
          >
            {dogs.map(dog => (
              <option key={dog.id} value={dog.id}>{dog.name} ({dog.breed})</option>
            ))}
          </select>
        </div>

        {/* Image Upload */}
        <div className="mb-4">
          <input
            type="file"
            accept="image/*"
            multiple
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {images.length > 0 ? (
            <div className="grid grid-cols-2 gap-2 mb-2">
              {images.map((url, idx) => (
                <div key={idx} className="relative aspect-square rounded-xl overflow-hidden">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => setImages(prev => prev.filter((_, i) => i !== idx))}
                    className="absolute top-2 right-2 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
              {images.length < 4 && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="aspect-square rounded-xl border-2 border-dashed border-[#EBE8E0] flex items-center justify-center hover:bg-[#F5F3EE]"
                >
                  {uploading ? (
                    <div className="animate-spin w-6 h-6 border-2 border-[#FF7555] border-t-transparent rounded-full" />
                  ) : (
                    <Plus className="w-8 h-8 text-[#7A7571]" />
                  )}
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full h-40 rounded-xl border-2 border-dashed border-[#EBE8E0] flex flex-col items-center justify-center hover:bg-[#F5F3EE] transition"
            >
              {uploading ? (
                <div className="animate-spin w-8 h-8 border-2 border-[#FF7555] border-t-transparent rounded-full" />
              ) : (
                <>
                  <Camera className="w-10 h-10 text-[#7A7571] mb-2" />
                  <p className="text-[#7A7571]">Add Photos</p>
                </>
              )}
            </button>
          )}
        </div>

        {/* Caption */}
        <textarea
          value={caption}
          onChange={e => setCaption(e.target.value)}
          placeholder="What's your pup up to? 🐕"
          className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-24 mb-4"
          maxLength={500}
        />

        {/* Post Button */}
        <Button
          onClick={handlePost}
          disabled={posting || uploading}
          className="w-full h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl font-semibold"
        >
          {posting ? 'Posting...' : 'Share Post'}
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Post Card Component
const PostCard = ({ post, onLike, onComment, user }) => {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const res = await fetch(`${API_URL}/api/puppoto/feed/${post.id}/comments`);
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleComment = async () => {
    if (!newComment.trim() || !user) return;

    try {
      const res = await fetch(`${API_URL}/api/puppoto/feed/${post.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newComment })
      });

      if (res.ok) {
        const data = await res.json();
        setComments(prev => [...prev, data.comment]);
        setNewComment('');
      }
    } catch (error) {
      toast.error('Failed to comment');
    }
  };

  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-[#EBE8E0]"
    >
      {/* Header */}
      <div className="p-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full overflow-hidden bg-[#FF7555]/10">
          {post.dog?.photos?.[0] ? (
            <img src={post.dog.photos[0]} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Dog className="w-5 h-5 text-[#FF7555]" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <p className="font-semibold text-[#1A1817]">{post.dog_name || 'Unknown Pup'}</p>
          <p className="text-xs text-[#7A7571]">{post.dog?.breed} • {timeAgo(post.created_at)}</p>
        </div>
        <button className="p-2 hover:bg-[#F5F3EE] rounded-full">
          <MoreHorizontal className="w-5 h-5 text-[#7A7571]" />
        </button>
      </div>

      {/* Image */}
      {(post.image_url || post.images?.length > 0) && (
        <div className="aspect-square bg-[#F5F3EE]">
          <img 
            src={post.image_url || post.images[0]} 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Actions */}
      <div className="p-4">
        <div className="flex items-center gap-4 mb-3">
          <button 
            onClick={() => onLike(post.id)}
            className="flex items-center gap-1 group"
          >
            <Heart 
              className={`w-6 h-6 transition ${post.liked_by_me ? 'fill-[#FF7555] text-[#FF7555]' : 'text-[#1A1817] group-hover:text-[#FF7555]'}`}
            />
          </button>
          <button 
            onClick={() => { setShowComments(!showComments); if (!showComments) fetchComments(); }}
            className="flex items-center gap-1"
          >
            <MessageCircle className="w-6 h-6 text-[#1A1817]" />
          </button>
          <button className="flex items-center gap-1">
            <Share2 className="w-6 h-6 text-[#1A1817]" />
          </button>
          <div className="flex-1" />
          <button>
            <Bookmark className="w-6 h-6 text-[#1A1817]" />
          </button>
        </div>

        {/* Likes count */}
        <p className="font-semibold text-sm text-[#1A1817] mb-1">
          {post.likes_count || 0} likes
        </p>

        {/* Caption */}
        {post.caption && (
          <p className="text-sm text-[#1A1817]">
            <span className="font-semibold">{post.dog_name}</span> {post.caption}
          </p>
        )}

        {/* View comments link */}
        {post.comments_count > 0 && !showComments && (
          <button 
            onClick={() => { setShowComments(true); fetchComments(); }}
            className="text-sm text-[#7A7571] mt-1"
          >
            View all {post.comments_count} comments
          </button>
        )}

        {/* Comments Section */}
        <AnimatePresence>
          {showComments && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 pt-3 border-t border-[#EBE8E0]"
            >
              {loadingComments ? (
                <p className="text-sm text-[#7A7571]">Loading...</p>
              ) : (
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {comments.map(comment => (
                    <div key={comment.id} className="flex gap-2">
                      <div className="w-6 h-6 rounded-full overflow-hidden bg-[#F5F3EE]">
                        {comment.user_picture ? (
                          <img src={comment.user_picture} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-3 h-3 text-[#7A7571]" />
                          </div>
                        )}
                      </div>
                      <p className="text-sm flex-1">
                        <span className="font-semibold">{comment.user_name}</span> {comment.content}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Add comment */}
              {user && (
                <div className="flex gap-2 mt-3">
                  <Input
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 h-9 rounded-full text-sm"
                    onKeyPress={e => e.key === 'Enter' && handleComment()}
                  />
                  <Button
                    onClick={handleComment}
                    disabled={!newComment.trim()}
                    size="sm"
                    className="rounded-full bg-[#FF7555] hover:bg-[#E5684A]"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Main PupSocial Component
export default function PupSocial() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [myDogs, setMyDogs] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    const init = async () => {
      // Check auth
      try {
        const authRes = await fetch(`${API_URL}/api/puppoto/auth/me`, {
          credentials: 'include'
        });
        if (authRes.ok) {
          const userData = await authRes.json();
          setUser(userData);

          // Get user's dogs
          const dogsRes = await fetch(`${API_URL}/api/puppoto/my-dogs`, {
            credentials: 'include'
          });
          if (dogsRes.ok) {
            const dogsData = await dogsRes.json();
            setMyDogs(dogsData.dogs || []);
          }
        }
      } catch (error) {
        // Not logged in
      }

      // Fetch feed
      fetchFeed();
    };

    init();
  }, []);

  const fetchFeed = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/puppoto/feed?limit=20`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (postId) => {
    if (!user) {
      toast.error('Sign in to like posts');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/puppoto/feed/${postId}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => prev.map(p => 
          p.id === postId 
            ? { ...p, liked_by_me: data.liked, likes_count: p.likes_count + (data.liked ? 1 : -1) }
            : p
        ));
      }
    } catch (error) {
      toast.error('Failed to like');
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/puppoto/social';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Figtree', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#EBE8E0]">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/puppoto" className="flex items-center gap-2">
            <ChevronLeft className="w-5 h-5 text-[#7A7571]" />
          </a>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#FF7555] to-[#E5684A] rounded-xl flex items-center justify-center">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Pup Social
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="compact" />
            {user ? (
              <button 
                onClick={() => navigate('/puppoto/profile')}
                className="w-10 h-10 rounded-full bg-[#F5F3EE] flex items-center justify-center overflow-hidden"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-[#7A7571]" />
                )}
              </button>
            ) : (
              <button 
                onClick={handleGoogleLogin}
                className="w-10 h-10 rounded-full bg-[#FF7555] flex items-center justify-center"
              >
                <LogIn className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-[#FF7555] border-t-transparent rounded-full" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <Camera className="w-16 h-16 text-[#7A7571]/30 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#1A1817] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              No posts yet!
            </h3>
            <p className="text-[#7A7571] mb-4">Be the first to share your pup's adventures</p>
            {user && myDogs.length > 0 && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" /> Create Post
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {posts.map(post => (
              <PostCard 
                key={post.id} 
                post={post} 
                onLike={handleLike}
                user={user}
              />
            ))}
          </div>
        )}
      </main>

      {/* Floating Action Button */}
      {user && myDogs.length > 0 && (
        <button
          onClick={() => setShowCreateModal(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-[#FF7555] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#E5684A] transition z-30"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {/* Create Post Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreatePostModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            dogs={myDogs}
            onPostCreated={fetchFeed}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
